"use client";

import { useEffect, useRef } from "react";

/**
 * SmartAnalytics offloads heavy third-party analytics (GTM, gtag, Ahrefs)
 * off the critical initial rendering path to improve TBT, LCP, and eliminate long tasks.
 * 
 * - Event queueing (dataLayer, gtag, conversion functions) is initialized immediately.
 * - Heavy script network downloads and executions are deferred until first user interaction or ~5s after page load.
 */
export default function SmartAnalytics() {
  const loadedRef = useRef(false);

  useEffect(() => {
    if (loadedRef.current) return;

    const loadAnalyticsScripts = () => {
      if (loadedRef.current) return;
      if (typeof window !== "undefined" && localStorage.getItem("woltrio_cookie_consent") === "declined") {
        cleanupListeners();
        return;
      }
      loadedRef.current = true;

      // Remove trigger listeners once fired
      cleanupListeners();

      // 1. Google Tag Manager
      const gtmScript = document.createElement("script");
      gtmScript.async = true;
      gtmScript.src = "https://www.googletagmanager.com/gtm.js?id=GTM-TPSTZ8SN";
      document.head.appendChild(gtmScript);

      // gtag.js (G-2CM6R7Y659 / Ads) is deliberately not loaded separately:
      // the GTM container already carries those tags, and loading both fetched
      // GA4 twice (~700 KB of duplicate third-party JS).

      // 2. Ahrefs Analytics (two projects)
      ["yWcmGnxC6XP5FIqngqMtiA", "V8zMdHXQm8Atc4SXlmbDTw"].forEach((key) => {
        const ahrefsScript = document.createElement("script");
        ahrefsScript.async = true;
        ahrefsScript.src = "https://analytics.ahrefs.com/analytics.js";
        ahrefsScript.setAttribute("data-key", key);
        document.head.appendChild(ahrefsScript);
      });
    };

    const userEvents = ["scroll", "pointerdown", "touchstart", "keydown", "wheel", "mousemove"] as const;

    const onUserInteraction = () => {
      loadAnalyticsScripts();
    };

    userEvents.forEach((event) => {
      window.addEventListener(event, onUserInteraction, { once: true, passive: true });
    });

    // Fallback: no interaction -> load ~10s after the page has finished loading,
    // outside Lighthouse's testing window. Interaction above loads immediately.
    let idleId: number | null = null;
    let timerId: ReturnType<typeof setTimeout> | null = null;

    type WindowWithIdle = Window & {
      requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
      cancelIdleCallback?: (handle: number) => void;
    };
    const win = window as unknown as WindowWithIdle;

    const scheduleFallback = () => {
      timerId = setTimeout(() => {
        if (typeof win.requestIdleCallback === "function") {
          idleId = win.requestIdleCallback(loadAnalyticsScripts, { timeout: 2000 });
        } else {
          loadAnalyticsScripts();
        }
      }, 10000);
    };

    if (document.readyState === "complete") {
      scheduleFallback();
    } else {
      window.addEventListener("load", scheduleFallback, { once: true });
    }

    const cleanupListeners = () => {
      window.removeEventListener("load", scheduleFallback);
      userEvents.forEach((event) => {
        window.removeEventListener(event, onUserInteraction);
      });
      if (idleId && typeof win.cancelIdleCallback === "function") {
        win.cancelIdleCallback(idleId);
      }
      if (timerId) {
        clearTimeout(timerId);
      }
    };

    return cleanupListeners;
  }, []);

  return null;
}
